/* ==================================================================
   THE RING - 3D viewer

   A deliberately small subset of the landing page's WebGL stage: the
   model, the lighting rig, and a slow two axis drift. No postprocessing,
   no scroll hijacking, no shard system, no pointer interaction.

   Lighting is ported one to one from src/scene/Experience.tsx so the ring
   reads the same here as it does on the landing page:

     camera      position [0, 0, 6.5], fov 38
     spot        [6, 8, 6]   angle 0.50  penumbra 1  intensity 240  #fff3ea
     spot        [-7, 5, 4]  angle 0.40  penumbra 1  intensity 120  #ffe7da
     point       [-6, -2, 4] intensity 22  #ff2a18
     point       [4, -4, -6] intensity 12  #ff5533
     ambient     0.18
     directional [0, 2, 8] intensity 1.2  #fff8f2
     environment six emissive panels, PMREM filtered
     material    metalness 0.9, roughness 0.22, envMapIntensity 1.8

   Those intensities look enormous because three has been physically
   correct since r155. Do not "fix" them downward against an older
   tutorial, they are candela and they are correct for this rig.

   Cost control, in order of importance:
     - nothing loads until the section is near the viewport
     - the loop stops when the section leaves the viewport or the tab hides
     - reduced motion renders a single frame and never starts a loop
     - DPR is capped, and pinned to 1 where the device looks low powered
     - everything is disposed on shopify:section:unload
   ================================================================== */

import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";

const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Rough low power check. Mirrors the landing page's intent: do not push
   a high pixel ratio at a device that will drop frames for it. */
const LOW_POWER =
  (navigator.hardwareConcurrency || 8) <= 4 ||
  /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

function hasWebGL() {
  try {
    const c = document.createElement("canvas");
    return !!(
      window.WebGLRenderingContext &&
      (c.getContext("webgl2") || c.getContext("webgl"))
    );
  } catch (e) {
    return false;
  }
}

/* ------------------------------------------------------------------
   Environment

   The landing page builds its studio from drei Lightformers, which are
   just emissive planes rendered into a cube map. Rebuilt here with plain
   meshes so this file carries no React and no drei.
   ------------------------------------------------------------------ */
const PANELS = [
  { form: "rect", intensity: 9,   position: [0, 4, -4],  scale: [10, 2],    color: "#ffffff" },
  { form: "rect", intensity: 5,   position: [0, 2, 6],   scale: [12, 1.2],  color: "#fff0e8" },
  { form: "rect", intensity: 3,   position: [-5, 0, 2],  scale: [8, 1.2],   color: "#ffd9cc", rotY: Math.PI / 2 },
  { form: "rect", intensity: 4,   position: [5, -1, 1],  scale: [8, 1.5],   color: "#ffffff", rotY: -Math.PI / 2 },
  { form: "ring", intensity: 2,   position: [0, 0, 5],   scale: [4, 4],     color: "#ff3322" },
  { form: "rect", intensity: 1.5, position: [0, -5, 0],  scale: [10, 10],   color: "#330808", rotX: Math.PI / 2 }
];

function buildEnvironment(renderer) {
  const envScene = new THREE.Scene();

  for (const p of PANELS) {
    const geo =
      p.form === "ring"
        ? new THREE.RingGeometry(p.scale[0] * 0.35, p.scale[0] * 0.5, 48)
        : new THREE.PlaneGeometry(p.scale[0], p.scale[1]);

    /* drei folds intensity into the emissive colour rather than using a
       light, so the panel shows up in the cube map render */
    const color = new THREE.Color(p.color).multiplyScalar(p.intensity);
    const mat = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(p.position[0], p.position[1], p.position[2]);
    if (p.rotY) mesh.rotation.y = p.rotY;
    if (p.rotX) mesh.rotation.x = p.rotX;
    envScene.add(mesh);
  }

  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  const target = pmrem.fromScene(envScene, 0.04);

  // the source scene is only needed for that one render
  envScene.traverse((o) => {
    if (o.isMesh) {
      o.geometry.dispose();
      o.material.dispose();
    }
  });
  pmrem.dispose();

  return target.texture;
}

/* ------------------------------------------------------------------
   Viewer
   ------------------------------------------------------------------ */

class RingViewer {
  constructor(el) {
    this.el = el;
    this.src = el.dataset.model;
    this.running = false;
    this.frame = 0;
    this.disposed = false;

    this.speedY = parseFloat(el.dataset.speedY || "0.055");
    this.speedX = parseFloat(el.dataset.speedX || "0.022");
    this.tiltX = parseFloat(el.dataset.tiltX || "0.18");

    this.onVisibility = this.onVisibility.bind(this);
    this.onResize = this.onResize.bind(this);
    this.tick = this.tick.bind(this);
  }

  fail(reason) {
    this.el.classList.add("ring3d--failed");
    this.el.setAttribute("data-fail", reason);
    // the poster image underneath is already in the DOM, just reveal it
    const poster = this.el.querySelector("[data-ring3d-poster]");
    if (poster) poster.hidden = false;
  }

  async init() {
    if (!this.src) return this.fail("no-model");
    if (!hasWebGL()) return this.fail("no-webgl");

    const w = this.el.clientWidth || 1;
    const h = this.el.clientHeight || 1;

    this.renderer = new THREE.WebGLRenderer({
      antialias: !LOW_POWER,
      alpha: true,
      powerPreference: "high-performance"
    });
    this.renderer.setPixelRatio(LOW_POWER ? 1 : Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h, false);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.domElement.classList.add("ring3d__canvas");
    this.el.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.environment = buildEnvironment(this.renderer);

    this.camera = new THREE.PerspectiveCamera(38, w / h, 0.1, 60);
    this.camera.position.set(0, 0, 6.5);

    this.addLights();

    try {
      await this.loadModel();
    } catch (e) {
      console.warn("[ring-3d] model failed to load", e);
      return this.fail("model-error");
    }

    if (this.disposed) return;

    this.el.classList.add("ring3d--ready");
    this.onResize();
    window.addEventListener("resize", this.onResize, { passive: true });
    document.addEventListener("visibilitychange", this.onVisibility);

    /* Reduced motion still gets the object, just not the drift. One frame,
       then nothing runs. */
    if (REDUCED) {
      this.renderer.render(this.scene, this.camera);
      return;
    }
    this.start();
  }

  addLights() {
    const s1 = new THREE.SpotLight(0xfff3ea, 240, 0, 0.5, 1);
    s1.position.set(6, 8, 6);

    const s2 = new THREE.SpotLight(0xffe7da, 120, 0, 0.4, 1);
    s2.position.set(-7, 5, 4);

    const p1 = new THREE.PointLight(0xff2a18, 22);
    p1.position.set(-6, -2, 4);

    const p2 = new THREE.PointLight(0xff5533, 12);
    p2.position.set(4, -4, -6);

    const amb = new THREE.AmbientLight(0xffffff, 0.18);

    const dir = new THREE.DirectionalLight(0xfff8f2, 1.2);
    dir.position.set(0, 2, 8);

    this.scene.add(s1, s2, p1, p2, amb, dir);
  }

  loadModel() {
    const loader = new GLTFLoader();

    /* Draco is only wired up if the file turns out to be compressed. The
       decoder is fetched lazily by three itself, so an uncompressed glb
       costs nothing here. */
    const draco = new DRACOLoader();
    draco.setDecoderPath("https://cdn.jsdelivr.net/npm/three@0.169.0/examples/jsm/libs/draco/");
    loader.setDRACOLoader(draco);
    this.draco = draco;

    return new Promise((resolve, reject) => {
      loader.load(
        this.src,
        (gltf) => {
          if (this.disposed) return resolve();
          this.model = gltf.scene;

          this.model.traverse((child) => {
            if (!child.isMesh) return;
            const mat = child.material;
            if (!mat) return;
            // same surface treatment as the landing page
            mat.metalness = 0.9;
            mat.roughness = 0.22;
            mat.envMapIntensity = 1.8;
            mat.needsUpdate = true;
          });

          this.fitToView(this.model);
          this.scene.add(this.model);
          resolve();
        },
        undefined,
        reject
      );
    });
  }

  /* Centre the model and scale it so it fills a predictable share of the
     frame, whatever units it was exported in. Without this the ring is
     either a speck or clipping the near plane. */
  fitToView(obj) {
    const box = new THREE.Box3().setFromObject(obj);
    const size = box.getSize(new THREE.Vector3());
    const centre = box.getCenter(new THREE.Vector3());

    obj.position.sub(centre);

    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const target = parseFloat(this.el.dataset.fill || "2.6");
    const scale = target / maxDim;
    obj.scale.setScalar(scale);

    // a slight resting tilt so it never reads as a flat disc
    obj.rotation.x = this.tiltX;

    this.pivot = new THREE.Group();
  }

  start() {
    if (this.running || this.disposed || REDUCED) return;
    this.running = true;
    this.last = performance.now();
    this.frame = requestAnimationFrame(this.tick);
  }

  stop() {
    this.running = false;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  onVisibility() {
    if (document.hidden) this.stop();
    else if (this.inView) this.start();
  }

  tick(now) {
    if (!this.running || this.disposed) return;
    const dt = Math.min((now - this.last) / 1000, 0.1);
    this.last = now;

    if (this.model) {
      // two axes, both slow. this is drift, not a spin
      this.model.rotation.y += this.speedY * dt;
      this.model.rotation.x = this.tiltX + Math.sin(now * 0.00012) * this.speedX * 8;
    }

    this.renderer.render(this.scene, this.camera);
    this.frame = requestAnimationFrame(this.tick);
  }

  onResize() {
    if (!this.renderer || this.disposed) return;
    const w = this.el.clientWidth || 1;
    const h = this.el.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (!this.running) this.renderer.render(this.scene, this.camera);
  }

  dispose() {
    this.disposed = true;
    this.stop();
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("visibilitychange", this.onVisibility);

    if (this.model) {
      this.model.traverse((o) => {
        if (!o.isMesh) return;
        o.geometry && o.geometry.dispose();
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach((m) => {
          if (!m) return;
          for (const k in m) {
            const v = m[k];
            if (v && v.isTexture) v.dispose();
          }
          m.dispose();
        });
      });
    }
    if (this.scene && this.scene.environment) this.scene.environment.dispose();
    if (this.draco) this.draco.dispose();
    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
    }
    this.scene = this.camera = this.renderer = this.model = null;
  }
}

/* ------------------------------------------------------------------
   Wiring
   ------------------------------------------------------------------ */

const viewers = new WeakMap();

function mount(el) {
  if (viewers.has(el)) return;
  const viewer = new RingViewer(el);
  viewers.set(el, viewer);

  /* Nothing above is touched until the section is close to the viewport.
     rootMargin gives the model time to load before it is looked at. */
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        viewer.inView = entry.isIntersecting;
        if (entry.isIntersecting) {
          if (!viewer.renderer) viewer.init();
          else viewer.start();
        } else {
          viewer.stop();
        }
      }
    },
    { rootMargin: "200px 0px" }
  );
  io.observe(el);
  viewer.io = io;
}

function mountAll(root) {
  (root || document).querySelectorAll("[data-ring3d]").forEach(mount);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => mountAll());
} else {
  mountAll();
}

/* Theme editor: a section can be replaced at any moment. Drop the GL
   context rather than leaking one per edit. */
document.addEventListener("shopify:section:unload", (e) => {
  e.target.querySelectorAll("[data-ring3d]").forEach((el) => {
    const v = viewers.get(el);
    if (!v) return;
    if (v.io) v.io.disconnect();
    v.dispose();
    viewers.delete(el);
  });
});

document.addEventListener("shopify:section:load", (e) => mountAll(e.target));
